import React, { useState, useEffect, useCallback } from 'react';
import ScrollX from './ScrollX';
import { supabase } from '../supabase';
import { CLIENT_ASSIGNMENT_ROLE } from '../constants/roles';
import AssignmentPopover, { TsAssignmentDisplay, saveClientRole } from './AssignmentPopover';

// At-a-glance editable grid (Utilities → Table Management → Client Assignments). ALL clients in one table;
// CS/TS/CM edited inline via a popover — you never leave the grid. Single source of truth = client_role_assignments.
// The role popover, TS state-split, CM is_client_manager filter, and TS hover-bubble are the shared components.
// Consultants are NOT here (external, property-level, own portal).
export default function AssignmentGrid({ darkMode }) {
  const isDark = darkMode;
  const c = isDark
    ? { card: 'rgba(47,56,84,0.6)', border: '#374060', text: '#e8eaf2', muted: 'var(--slate)', th: 'rgba(26,34,56,0.5)', cellHover: 'rgba(180,0,0,0.10)', input: 'var(--ink)', inputBorder: '#434C68' }
    : { card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', th: 'var(--line-soft)', cellHover: 'rgba(180,0,0,0.05)', input: '#ffffff', inputBorder: '#c8ccd8' };
  const s = {
    card: { background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', overflow: 'hidden', marginBottom: '16px', boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)' },
    cardH: { padding: '12px 18px', borderBottom: `1px solid ${c.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    cardT: { fontSize: '14px', fontWeight: '700', color: c.text },
    th: { padding: '9px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: c.muted, background: c.th, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' },
    td: { padding: '10px 16px', fontSize: '13px', color: c.text, borderBottom: `1px solid ${isDark ? 'rgba(67,76,104,0.25)' : '#eef0f6'}`, verticalAlign: 'top' },
    input: { width: '100%', padding: '7px 10px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Public Sans',system-ui,sans-serif" },
  };

  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);              // active ntrg_staff: {id,name,is_client_manager}
  const [assignments, setAssignments] = useState([]);  // client_role_assignments rows
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);        // { clientId, clientName, role }
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [cl, st, asg] = await Promise.all([
      supabase.from('clients').select('id,name').order('name'),
      supabase.from('ntrg_staff').select('id,name,is_client_manager').eq('active', true).order('name'),
      supabase.from('client_role_assignments').select('*'),
    ]);
    setClients(cl.data || []); setStaff(st.data || []); setAssignments(asg.data || []); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const reloadAssignments = useCallback(async () => {
    const { data } = await supabase.from('client_role_assignments').select('*');
    setAssignments(data || []);
  }, []);

  const staffName = (id) => (staff.find(x => x.id === id) || {}).name || '';
  const rowsFor = (clientId, role) => assignments.filter(a => a.client_id === clientId && a.role === role);
  const csDisplay = (id) => { const r = rowsFor(id, 'CS'); return r[0] ? staffName(r[0].staff_id) : ''; };
  const cmDisplay = (id) => rowsFor(id, 'CM').map(r => staffName(r.staff_id)).filter(Boolean).join(', ');

  const onSave = async (rows) => {
    try {
      await saveClientRole(editing.clientId, editing.role, rows);
      await reloadAssignments();
      setEditing(null);
    } catch (e) { window.alert('Assignment failed: ' + (e.message || e)); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q ? clients.filter(cl => (cl.name || '').toLowerCase().includes(q)) : clients;

  if (loading) return <div style={{ padding: '24px', color: c.muted, fontSize: '13px' }}>Loading…</div>;

  const cell = (cl, role, content) => (
    <td style={{ ...s.td, cursor: 'pointer' }} title="Click to edit"
      onClick={() => setEditing({ clientId: cl.id, clientName: cl.name, role })}
      onMouseEnter={e => e.currentTarget.style.background = c.cellHover}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      {content}
    </td>
  );
  const orDash = (text) => text ? <span>{text}</span> : <span style={{ color: c.muted }}>—</span>;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '22px', fontWeight: '700', color: c.text, marginBottom: '4px' }}>Client Assignments</div>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '16px' }}>Internal staff assigned to each client: Client Specialist, Tax Specialist (state-aware), and Client Manager(s). Click a cell to edit. External consultants are managed separately.</div>

      <div style={{ marginBottom: '14px', maxWidth: '320px' }}>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients…" style={s.input} />
      </div>

      <div style={s.card}>
        <div style={s.cardH}><div style={s.cardT}>Clients</div><span style={{ fontSize: '12px', color: c.muted }}>{filtered.length} shown</span></div>
        <ScrollX style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={s.th}>Client</th><th style={s.th}>CM(s)</th><th style={s.th}>CS</th><th style={s.th}>TS</th></tr></thead>
            <tbody>
              {filtered.length === 0
                ? <tr><td colSpan="4" style={{ ...s.td, textAlign: 'center', color: c.muted }}>{q ? `No clients match “${search.trim()}”.` : 'No clients.'}</td></tr>
                : filtered.map(cl => (
                  <tr key={cl.id}>
                    <td style={{ ...s.td, fontWeight: '600', color: c.text }}>{cl.name}</td>
                    {cell(cl, CLIENT_ASSIGNMENT_ROLE.CM, orDash(cmDisplay(cl.id)))}
                    {cell(cl, CLIENT_ASSIGNMENT_ROLE.CS, orDash(csDisplay(cl.id)))}
                    {cell(cl, CLIENT_ASSIGNMENT_ROLE.TS, <TsAssignmentDisplay rows={rowsFor(cl.id, 'TS')} staffName={staffName} darkMode={darkMode} />)}
                  </tr>
                ))}
            </tbody>
          </table>
        </ScrollX>
      </div>

      {editing && (
        <AssignmentPopover
          role={editing.role}
          clientName={editing.clientName}
          staff={staff}
          currentRows={rowsFor(editing.clientId, editing.role)}
          onSave={onSave}
          onClose={() => setEditing(null)}
          darkMode={darkMode} />
      )}
    </div>
  );
}
